import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Calendar, 
  
  Tag,
} from '@phosphor-icons/react';
import { getBlogPost } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import NativeShareButton from '@/components/NativeShareButton';

const FALLBACK_IMG = 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=1920';

const cleanBlogContent = (html) => {
  if (!html) return '';
  return html
    // Remove empty wp-block-image figures (no img inside)
    .replace(/<figure[^>]*class="wp-block-image"[^>]*>\s*<\/figure>/gi, '')
    // Remove broken addthis scripts and images
    .replace(/<a[^>]*addthis[^>]*>.*?<\/a>/gi, '')
    .replace(/<script[^>]*addthis[^>]*>.*?<\/script>/gi, '')
    // Strip inline font-size and text-align overrides from WP content
    .replace(/style="[^"]*font-size:\s*\d+p[tx][^"]*"/gi, '')
    .replace(/style="[^"]*text-align:\s*justify[^"]*"/gi, '')
    // Clean remaining empty style attributes
    .replace(/\s*style=""/gi, '')
    // Convert non-breaking spaces (\u00A0 / &nbsp;) to regular spaces so words can wrap on mobile.
    // WordPress often inserts &nbsp; between words which prevents natural line breaks
    // and forces mid-word breaks on narrow screens.
    .replace(/\u00A0/g, ' ')
    .replace(/&nbsp;/gi, ' ');
};

const resolveImg = (src) => {
  if (!src) return FALLBACK_IMG;
  return src;
};
import SEO from '@/components/SEO';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await getBlogPost(slug);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const shareUrl = `https://www.wineryfinderapp.com/blog/${slug}`;

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <Helmet>
          <link rel="canonical" href={`https://www.wineryfinderapp.com/blog/${slug}`} />
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center">
        <h1 className="font-serif text-2xl text-primary mb-4">Article Not Found</h1>
        <button onClick={() => navigate(-1)} className="btn-primary">
          <ArrowLeft size={18} className="mr-2" />
          Back to Blog
        </button>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={post?.meta_title || post?.title || 'Blog Post'}
        description={post?.meta_description || post?.excerpt || ''}
        path={`/blog/${slug}`}
        image={resolveImg(post?.featured_image)}
        type="article"
        article={{
          title: post?.title,
          excerpt: post?.excerpt,
          image: resolveImg(post?.featured_image),
          publishedAt: post?.created_at,
          updatedAt: post?.updated_at,
          category: post?.category
        }}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: post?.title || 'Article', path: `/blog/${slug}` }
        ]}
      />

      {/* Hero Image */}
      <section 
        className="relative h-[40vh] sm:h-[50vh] min-h-[300px] sm:min-h-[400px]"
        data-testid="blog-post-hero"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6)), url('${resolveImg(post.featured_image)}')`
          }}
        />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-8 sm:pb-12 w-full">
            <button 
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-3 sm:mb-4 transition-colors cursor-pointer bg-transparent border-0 text-sm"
              data-testid="back-to-blog"
            >
              <ArrowLeft size={16} />
              Back to Blog
            </button>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-gold-400 text-xs sm:text-sm font-medium uppercase tracking-wide">
                {post.category}
              </span>
              <h1 className="font-serif text-2xl sm:text-3xl md:text-5xl text-white mt-1 sm:mt-2 mb-2 sm:mb-4 leading-tight">
                {post.title}
              </h1>
              <div className="flex items-center gap-4 text-white/70 text-xs sm:text-sm">
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {formatDate(post.created_at)}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-6 sm:py-12" data-testid="blog-post-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,200px] gap-6 sm:gap-12">
            {/* Main Content */}
            <div className="min-w-0">
              <div 
                className="blog-content prose-wine"
                dangerouslySetInnerHTML={{ __html: cleanBlogContent(post.content) }}
              />

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-8 pt-8 border-t border-border">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag size={18} className="text-muted-foreground" />
                    {post.tags.map((tag) => (
                      <span 
                        key={tag}
                        className="bg-muted text-muted-foreground text-sm px-3 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-8 pt-8 border-t border-border">
                <NativeShareButton url={shareUrl} title={post.title} />
              </div>
            </div>

          </div>
        </div>
      </article>

    </>
  );
};

export default BlogPost;
